import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { TitleService } from '@core/services/hasTitle.service';
import { HeaderComponent } from '@ui/components/header.component';
import { SidebarComponent } from '@ui/components/sidebar.component';
import { LoaderComponent } from '@shared/components/loader/loader.component';
import { CommonModule } from '@angular/common';
import { LanguageService } from '@core/services/language.service';
import { LoaderService } from '@core/services/loader.service';
import { NotificationService, AppNotification } from '@core/services/notification.service';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { SiteService } from '@data-access/services/site.service';
import { Site } from '@core/models/site.model';
import { SiteSelectionModalComponent } from '@features/inventory-settings/components/site-selection-modal.component';
import { routeAnimation } from '@shared/animations';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SidebarComponent, HeaderComponent, LoaderComponent, CommonModule, SiteSelectionModalComponent],
  templateUrl: './app.html',
  animations: [routeAnimation]
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('demande');
  isSidebarOpen = false;
  showLayout = true;
  showSiteSelectionModal = false;
  siteSelectionAllowDismiss = true;

  notifications: AppNotification[] = [];
  private notificationSub?: Subscription;

  constructor(
    private titleService: TitleService,
    private route: ActivatedRoute,
    private router: Router,
    private languageService: LanguageService,
    public loaderService: LoaderService,
    private siteService: SiteService,
    private notificationService: NotificationService
  ) {
    const titleIsSet = this.titleService.getHasTitleFromUrl();
    this.titleService.setHasTitle(titleIsSet);
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      const lang = params['lang'];
      if (lang) {
        this.languageService.setLanguage(lang);
      }
    });

    // Check site selection
    this.siteService.loadSelectedSiteFromStorage();
    if (!this.siteService.hasSiteSelected()) {
      // Load sites to check the number
      this.siteService.loadSites();

      // Subscribe to auto-select if only one site
      this.siteService.sites$.pipe(
        filter(sites => sites.length > 0),
        take(1) // Take only the first emission
      ).subscribe(sites => {
        if (sites.length === 1) {
          // Auto-select the only available site
          this.siteService.selectSite(sites[0]);
        } else {
          // Show modal for manual selection
          this.showSiteSelectionModal = true;
          this.siteSelectionAllowDismiss = false; // Force selection
        }
      });
    }

    // Listen to route changes to hide/show layout
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Hide layout on unauthorized and 404 pages
        this.showLayout = !event.url.includes('/unauthorized') && !event.url.includes('/404');
      });

    // Check initial route
    this.showLayout = !this.router.url.includes('/unauthorized') && !this.router.url.includes('/404');

    // Subscribe to notifications
    this.notificationSub = this.notificationService.notifications$.subscribe(notification => {
      this.notifications.push(notification);
      if (notification.duration) {
        setTimeout(() => this.dismissNotification(notification.id), notification.duration);
      }
    });
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  closeSidebar() {
    this.isSidebarOpen = false;
  }

  onSiteSelected(site: Site) {
    this.siteService.selectSite(site);
    this.showSiteSelectionModal = false;
  }

  onSiteSelectionCancelled() {
    this.showSiteSelectionModal = false;
  }

  dismissNotification(id: number): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
  }

  getNotificationClasses(type: string): string {
    const base = 'flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium';
    switch (type) {
      case 'success': return `${base} bg-green-50 text-green-800 border border-green-200`;
      case 'error': return `${base} bg-red-50 text-red-800 border border-red-200`;
      case 'warning': return `${base} bg-yellow-50 text-yellow-800 border border-yellow-200`;
      case 'info': return `${base} bg-blue-50 text-blue-800 border border-blue-200`;
      default: return `${base} bg-gray-50 text-gray-800 border border-gray-200`;
    }
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.['animation'];
  }

  ngOnDestroy(): void {
    this.notificationSub?.unsubscribe();
  }
}
